import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Play, Trash2, Download, Clock as ClockIcon, HardDrive } from "lucide-react";
import { toast } from "sonner";

const ENTITIES = ["users", "user-records", "pays", "regions", "sites", "societes", "directions", "services", "equipes", "profiles"];
const FREQUENCIES = [
  { value: "*/15 * * * *", label: "Toutes les 15 min" },
  { value: "0 * * * *", label: "Chaque heure" },
  { value: "0 6 * * *", label: "Quotidien à 06:00" },
  { value: "0 2 * * *", label: "Quotidien à 02:00" },
  { value: "0 4 * * 1", label: "Lundi à 04:00" },
];

export default function ScheduledExports() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: schedules } = useQuery({
    queryKey: ["scheduled-exports"],
    queryFn: async () => (await api.get("/scheduled-exports")).data.data,
    refetchInterval: 10000,
  });

  const { data: sftpFiles } = useQuery({
    queryKey: ["sftp-files"],
    queryFn: async () => (await api.get("/scheduled-exports/sftp-outbox")).data.data,
    refetchInterval: 10000,
  });

  const toggle = async (id, enabled) => {
    await api.patch(`/scheduled-exports/${id}`, { enabled });
    qc.invalidateQueries({ queryKey: ["scheduled-exports"] });
  };
  const runNow = async (id) => {
    await api.post(`/scheduled-exports/${id}/run`);
    toast.success("Export lancé");
    qc.invalidateQueries();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet export planifié ?")) return;
    await api.delete(`/scheduled-exports/${id}`);
    qc.invalidateQueries({ queryKey: ["scheduled-exports"] });
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="scheduled-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-terracotta-light text-terracotta text-[12px] font-medium mb-3">
            Mode legacy
          </div>
          <h1 className="font-heading text-[40px] font-semibold text-warm-dark tracking-tight">Exports planifiés</h1>
          <p className="text-[15px] text-warm-dark/70 mt-1">Dépose de CSV réguliers sur le dossier SFTP simulé — compatible avec les ingesteurs NIM batch.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="btn-new-schedule">
          <Plus size={14} strokeWidth={1.5} className="mr-1.5" />Nouvel export planifié
        </Button>
      </div>

      <div className="grid grid-cols-[1fr_360px] gap-5">
        <Card className="border-sand shadow-soft rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-sand bg-cream/50 text-[12px] font-semibold uppercase tracking-wider text-warm-dark/60">
            Plannings actifs
          </div>
          <div className="divide-y divide-sand">
            {(schedules || []).length === 0 && <div className="p-10 text-center text-warm-dark/50 text-[14px]">Aucun export planifié.</div>}
            {(schedules || []).map((s) => (
              <div key={s.id} className="p-5 flex items-start gap-5" data-testid={`schedule-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-[18px] font-semibold text-warm-dark">{s.name}</span>
                    <span className="px-2 py-0.5 bg-terracotta-light text-terracotta text-[11px] rounded font-medium">{s.entity}</span>
                    <span className="text-[12px] text-warm-dark/60 flex items-center gap-1"><ClockIcon size={11} strokeWidth={1.5} />{FREQUENCIES.find(f => f.value === s.cron)?.label || s.cron}</span>
                  </div>
                  <div className="text-[12px] text-warm-dark/60 font-mono mt-1.5">{s.filename_pattern}</div>
                  <div className="text-[11px] text-warm-dark/50 mt-1">
                    Dernière exécution : {s.last_run ? new Date(s.last_run).toLocaleString("fr-FR") : "jamais"}
                    {s.last_status && <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.last_status === "success" ? "bg-sage-light text-sage" : "bg-error/10 text-error"}`}>{s.last_status}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={s.enabled} onCheckedChange={(v) => toggle(s.id, v)} data-testid={`schedule-toggle-${s.id}`} />
                  <Button size="sm" variant="outline" onClick={() => runNow(s.id)} data-testid={`schedule-run-${s.id}`}><Play size={13} strokeWidth={1.5} /></Button>
                  <button onClick={() => remove(s.id)} className="text-warm-dark/50 hover:text-error p-1.5" data-testid={`schedule-del-${s.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-sand shadow-soft rounded-xl overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-sand bg-cream/50 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-warm-dark/60">
            <HardDrive size={12} strokeWidth={1.5} />
            Sortie SFTP
            <span className="ml-auto text-[10px] normal-case font-mono text-warm-dark/50">/outbox/maisonea</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {(sftpFiles || []).length === 0 && <div className="p-10 text-center text-warm-dark/50 text-[13px]">Aucun fichier généré.</div>}
            {(sftpFiles || []).map((f) => (
              <div key={f.id} className="px-5 py-3 border-b border-sand last:border-0 flex items-center gap-3" data-testid={`sftp-file-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[12px] text-warm-dark truncate">{f.filename}</div>
                  <div className="text-[11px] text-warm-dark/50 mt-0.5">{new Date(f.generated_at).toLocaleString("fr-FR")} · {(f.size_bytes / 1024).toFixed(1)} KB</div>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-exports/download/${f.id}?token=${localStorage.getItem("sirh_token")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-terracotta hover:text-terracotta-dark"
                  data-testid={`sftp-dl-${f.id}`}
                >
                  <Download size={14} strokeWidth={1.5} />
                </a>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function CreateDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", entity: "users", cron: "0 6 * * *", filename_pattern: "SIRH_USERS_{YYYY-MM-DD-HHmm}.csv", separator: ";", bom: true });
  const submit = async () => {
    if (!form.name) return;
    await api.post("/scheduled-exports", { ...form, enabled: true });
    toast.success("Export planifié créé");
    qc.invalidateQueries({ queryKey: ["scheduled-exports"] });
    onClose();
    setForm({ name: "", entity: "users", cron: "0 6 * * *", filename_pattern: "SIRH_USERS_{YYYY-MM-DD-HHmm}.csv", separator: ";", bom: true });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-off-white" data-testid="schedule-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">Nouvel export planifié</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Nom"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sch-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entité"><Select value={form.entity} onValueChange={(v) => setForm({ ...form, entity: v })}><SelectTrigger data-testid="sch-entity"><SelectValue /></SelectTrigger><SelectContent>{ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Fréquence"><Select value={form.cron} onValueChange={(v) => setForm({ ...form, cron: v })}><SelectTrigger data-testid="sch-cron"><SelectValue /></SelectTrigger><SelectContent>{FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent></Select></Field>
          </div>
          <Field label="Pattern de fichier (placeholders: {YYYY}, {MM}, {DD}, {HH}, {mm})">
            <Input value={form.filename_pattern} onChange={(e) => setForm({ ...form, filename_pattern: e.target.value })} className="font-mono" data-testid="sch-pattern" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Séparateur"><Select value={form.separator} onValueChange={(v) => setForm({ ...form, separator: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value=";">;</SelectItem><SelectItem value=",">,</SelectItem><SelectItem value="\t">Tab</SelectItem></SelectContent></Select></Field>
            <Field label="BOM UTF-8"><div className="h-10 flex items-center"><Switch checked={form.bom} onCheckedChange={(v) => setForm({ ...form, bom: v })} /></div></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="sch-submit">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-[12px] font-medium text-warm-dark mb-1.5 block">{label}</Label>{children}</div>;
}
